<script setup lang="ts">
import { PayRatePeriod } from '@factoryfixinc/ats-interfaces';
import { nextTick, ref, watch } from 'vue';
import ChevronIcon from '@/assets/svg/conversations/ChevronIcon.svg?component';
import MessageTemplate from '@/components/Shared/Input/Template/MessageTemplate.vue';
import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import { PayRatePeriodItems } from '@/core/jobs/copilot-activation/local-job-data.type';
import {
  isRequired,
  isValidMaxPayRate,
  isValidMaxSalaryPayRate,
  positiveOrZero,
} from '@/utils/forms';
import type { VTextField } from 'vuetify/components';

const props = defineProps<{
  disabled: boolean;
}>();

const copilotActivationService = new CopilotActivationService();
const payRateMinInput = ref<VTextField | null>(null);
const payRateMaxInput = ref<VTextField | null>(null);
const payRateSalaryMinInput = ref<VTextField | null>(null);
const payRateSalaryMaxInput = ref<VTextField | null>(null);

watch(
  () => copilotActivationService.payRateMin,
  () => {
    const hasPayRateMaxValue = copilotActivationService.payRateMax !== null;

    if (payRateMinInput.value && (payRateMaxInput.value?.dirty || hasPayRateMaxValue)) {
      nextTick(() => {
        payRateMaxInput.value?.validate();
      });
    }
  },
);

watch(
  () => copilotActivationService.payRateSalaryMin,
  () => {
    const hasPayRateSalaryMaxValue = copilotActivationService.payRateSalaryMax !== null;

    if (
      payRateSalaryMinInput.value &&
      (payRateSalaryMaxInput.value?.dirty || hasPayRateSalaryMaxValue)
    ) {
      nextTick(() => {
        payRateSalaryMaxInput.value?.validate();
      });
    }
  },
);
</script>

<template>
  <div class="pay-range-row flex flex-wrap gap-x-[5px] gap-y-2">
    <template v-if="copilotActivationService.payRatePeriod === PayRatePeriod.HOURLY">
      <div class="w-[120px] flex-none">
        <v-text-field
          v-model="copilotActivationService.payRateMin"
          ref="payRateMinInput"
          placeholder="Min"
          variant="outlined"
          density="compact"
          type="number"
          validate-on="submit"
          :rules="[positiveOrZero]"
          :disabled="props.disabled"
        >
          <template #message="{ message }">
            <MessageTemplate :message="message" />
          </template>
        </v-text-field>
      </div>
      <p class="pt-2">-</p>
      <div class="w-[120px] flex-none">
        <v-text-field
          v-model="copilotActivationService.payRateMax"
          ref="payRateMaxInput"
          placeholder="Max"
          variant="outlined"
          density="compact"
          type="number"
          validate-on="submit"
          :rules="[
            positiveOrZero,
            isValidMaxPayRate(
              copilotActivationService.payRateMin,
              copilotActivationService.payRateMax,
            ),
          ]"
          :disabled="props.disabled"
        >
          <template #message="{ message }">
            <MessageTemplate :message="message" />
          </template>
        </v-text-field>
      </div>
    </template>

    <template v-else>
      <div class="w-[120px] flex-none">
        <v-text-field
          v-model="copilotActivationService.payRateSalaryMin"
          ref="payRateSalaryMinInput"
          placeholder="Min"
          variant="outlined"
          density="compact"
          type="number"
          validate-on="submit"
          :rules="[positiveOrZero]"
          :disabled="props.disabled"
        >
          <template #message="{ message }">
            <MessageTemplate :message="message" />
          </template>
        </v-text-field>
      </div>
      <p class="pt-2">-</p>
      <div class="w-[120px] flex-none">
        <v-text-field
          v-model="copilotActivationService.payRateSalaryMax"
          ref="payRateSalaryMaxInput"
          placeholder="Max"
          variant="outlined"
          density="compact"
          type="number"
          validate-on="submit"
          :rules="[
            positiveOrZero,
            isValidMaxSalaryPayRate(
              copilotActivationService.payRateSalaryMin,
              copilotActivationService.payRateSalaryMax,
            ),
          ]"
          :disabled="props.disabled"
        >
          <template #message="{ message }">
            <MessageTemplate :message="message" />
          </template>
        </v-text-field>
      </div>
    </template>

    <div class="pay-range-row__freq min-w-[160px] max-w-[257px] flex-grow">
      <v-autocomplete
        v-model:model-value="copilotActivationService.payRatePeriod"
        :items="PayRatePeriodItems"
        item-title="text"
        item-value="value"
        variant="outlined"
        density="compact"
        placeholder="Frequency"
        :menu-icon="ChevronIcon"
        :return-object="false"
        :menu-props="{ maxWidth: '536px' }"
        :rules="[isRequired]"
        :disabled="props.disabled"
        validate-on="submit"
      >
        <template #message="{ message }">
          <MessageTemplate :message="message" />
        </template>
      </v-autocomplete>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pay-range-row {
  container-type: inline-size;
}

.v-input :deep(.v-field),
.v-input--horizontal :deep(.v-field) {
  border-radius: 6px;

  .v-autocomplete__selection,
  .v-combobox__selection {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }

  .v-field__outline {
    @apply text-tint-80;
  }
  input {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }
  input::placeholder {
    @apply text-shade-800;
    opacity: 1;
  }

  &:hover {
    .v-field__outline {
      @apply text-shade-840;
    }
  }
}

.v-input :deep(.v-field.v-field--focused),
.v-input--horizontal :deep(.v-field.v-field--focused) {
  .v-field__outline {
    @apply text-highlight-500;
  }
}

.v-input :deep(.v-field.v-field--error),
.v-input--horizontal :deep(.v-field.v-field--error) {
  .v-field__outline {
    @apply text-critical-500;
  }
}
</style>

<style lang="scss">
/*
 * Unscoped: :has(.v-field--error) must reach Vuetify's child-component
 * internals, which never receive this SFC's scoped [data-v-*] attribute.
 * The `.pay-range-row__freq` class is unique to this component, so a
 * global rule has no collision risk.
 */
@container (max-width: 420px) {
  .pay-range-row:not(:has(.v-field--error)) .pay-range-row__freq {
    margin-top: -16px;
  }
}
</style>
